import React, {
  useEffect,
  useRef,
  useState
} from 'react';

import axios from 'axios';

import L from 'leaflet';

import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  Polyline,
  Popup,
  Tooltip,
  useMap
} from 'react-leaflet';

import 'leaflet/dist/leaflet.css';


import {
  useParams
} from 'react-router-dom';

import {
  AlertTriangle,
  Building2,
  ChevronDown,
  ChevronUp,
  CloudSun,
  Droplets,
  FlaskConical,
  HeartPulse,
  MapPin,
  Navigation,
  Search,
  ShieldAlert,
  ShieldCheck,
  Thermometer,
  Wind
} from 'lucide-react';

import {
  Card
} from '../components/ui/card';

import {
  Input
} from '../components/ui/input';

import {
  Badge
} from '../components/ui/badge';

import {
  Button
} from '../components/ui/button';

import ModonLogo from '../components/ModonLogo';
import NFPADiamond from '../components/NFPADiamond';
import { GHSIconRenderer } from '../components/GHSIcons';
import './FireResponsePortal.css';


const unitLabel = (u) => ({
  liter: 'لتر',
  kg: 'كجم',
  ton: 'طن',
  count: 'عبوة',
}[u] || u || '');


function formatNumber(value) {

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return value ?? '—';
  }

  return new Intl.NumberFormat(
    'ar-SA',
    {
      maximumFractionDigits: 2
    }
  ).format(n);
}


function ChemicalQuantity({
  chem,
  compact = false,
  detail = false
}) {

  const quantity =
    chem?.quantity;

  const unit =
    chem?.unit || '';

  const packageCount =
    Number(
      chem?.packageCount || 0
    );

  const capacityL =
    Number(
      chem?.packageCapacityL || 0
    );

  const totalLiters =
    Number(
      chem?.totalLiters || 0
    );

  const packageLabel =
    chem?.packageLabel ||
    'عبوة';


  const formatValue = (value) => {

    const n = Number(value);

    if (!Number.isFinite(n)) {
      return value ?? '—';
    }

    return new Intl.NumberFormat(
      'ar-SA',
      {
        maximumFractionDigits: 2
      }
    ).format(n);
  };


  let actualValue = '—';
  let packageText = '';


  // إذا أدخل المصنع عدد العبوات:
  // نحسب الحجم الحقيقي من العدد × سعة العبوة.
  if (
    unit === 'count'
    && totalLiters > 0
  ) {

    actualValue =
      `${formatValue(totalLiters)} لتر`;

    packageText =
      `${formatValue(packageCount)} ${packageLabel}`;

    if (capacityL > 0) {
      packageText +=
        ` × ${formatValue(capacityL)} لتر`;
    }

  } else {

    // إذا الكمية مسجلة كلتر / كجم / طن
    if (
      quantity != null
      && quantity !== ''
    ) {

      actualValue =
        `${formatValue(quantity)} ${unitLabel(unit)}`.trim();

    }


    if (packageCount > 0) {

      packageText =
        `${formatValue(packageCount)} ${packageLabel}`;

      if (capacityL > 0) {
        packageText +=
          ` • سعة العبوة ${formatValue(capacityL)} لتر`;
      }

    }

  }


  // --------------------------------------------
  // Professional detail card
  // --------------------------------------------

  if (detail) {

    return (
      <div className="rounded-xl border border-[#1a4d80]/15 bg-blue-50/60 px-2.5 py-2">

        <div className="text-[9px] font-bold text-slate-500 mb-0.5">
          الكمية الفعلية الموجودة
        </div>

        <div
          className="text-base font-black text-[#1a4d80] leading-tight"
          dir="rtl"
        >
          {actualValue}
        </div>


        {packageText && (

          <div className="mt-1.5 inline-flex items-center rounded-md bg-white border border-slate-200 px-2 py-1 text-[10px] font-bold text-slate-700">

            {packageText}

          </div>

        )}


        {unit === 'count' &&
         totalLiters <= 0 && (

          <div className="mt-2 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">

            عدد العبوات مسجل، لكن سعة العبوة غير محددة لذلك لا يمكن حساب اللترات.

          </div>

        )}

      </div>
    );
  }


  // --------------------------------------------
  // Compact card used outside details
  // --------------------------------------------

  return (
    <div
      className={
        compact
          ? 'text-left shrink-0'
          : 'text-center'
      }
    >

      <div className="font-extrabold whitespace-nowrap text-[#1a4d80]">

        {actualValue}

      </div>


      {packageText && (

        <div className="text-[10px] text-slate-500 mt-0.5 whitespace-nowrap">

          {packageText}

        </div>

      )}

    </div>
  );
}

function ChemicalHazardMarks({
  chem,
  compact = false
}) {

  const nfpa =
    chem?.nfpa || {};

  const symbols =
    Array.isArray(
      chem?.ghsSymbols
    )
      ? chem.ghsSymbols
      : [];

  const hasNfpa =
    nfpa.health != null ||
    nfpa.fire != null ||
    nfpa.reactivity != null ||
    (
      nfpa.special &&
      nfpa.special !== '-'
    );


  if (
    !hasNfpa &&
    symbols.length === 0
  ) {
    return null;
  }


  return (
    <div
      className="flex items-center gap-1 flex-wrap"
      dir="ltr"
    >

      {hasNfpa && (
        <div
          className="shrink-0"
          title="NFPA 704"
        >
          <NFPADiamond
            health={
              nfpa.health ?? 0
            }
            fire={
              nfpa.fire ?? 0
            }
            reactivity={
              nfpa.reactivity ?? 0
            }
            special={
              nfpa.special || '-'
            }
            width={
              compact
                ? 38
                : 58
            }
          />
        </div>
      )}


      {symbols.length > 0 && (
        <div
          className="flex items-center gap-0.5 flex-wrap"
          title="رموز GHS"
        >
          <GHSIconRenderer
            symbols={symbols}
            size={
              compact
                ? 28
                : 40
            }
          />
        </div>
      )}

    </div>
  );
}


const riskClass = (
  level
) => {

  if (level === 'high') {
    return 'bg-red-600 text-white';
  }

  if (level === 'medium') {
    return 'bg-amber-500 text-white';
  }

  return 'bg-emerald-600 text-white';
};


function readable(value) {

  if (
    value == null ||
    value === ''
  ) {
    return '';
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number'
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {

    return value
      .map(readable)
      .filter(Boolean)
      .join(' • ');
  }

  if (
    typeof value === 'object'
  ) {

    if (value.text) {
      return readable(
        value.text
      );
    }

    if (value.items) {
      return readable(
        value.items
      );
    }

    return Object.values(value)
      .map(readable)
      .filter(Boolean)
      .join(' • ');
  }

  return '';
}


function WeatherBar({
  weather,
  title = 'الطقس الحالي'
}) {

  if (
    !weather ||
    !weather.available
  ) {

    return (
      <Card className="p-3 border-slate-200">

        <div className="flex items-center gap-2 text-sm text-slate-500">

          <CloudSun size={18}/>

          بيانات الطقس غير متاحة حاليًا

        </div>

      </Card>
    );
  }


  return (
    <Card className="overflow-hidden">

      <div className="bg-gradient-to-l from-sky-50 to-blue-50 px-3 py-2 border-b">

        <div className="flex items-center justify-between">

          <div className="flex items-center gap-2 font-bold text-[#1a4d80]">

            <CloudSun size={18}/>

            {title}

          </div>

          <div className="font-extrabold text-sm">
            {weather.condition}
          </div>

        </div>

      </div>


      <div className="grid grid-cols-2 sm:grid-cols-4">

        <div className="p-3 border-l border-b sm:border-b-0">

          <div className="flex items-center gap-1 text-[10px] text-slate-500">

            <Thermometer size={14}/>

            الحرارة

          </div>

          <div className="font-extrabold text-lg mt-1">

            {weather.temperatureC ?? '—'}°

          </div>

        </div>


        <div className="p-3 border-l border-b sm:border-b-0">

          <div className="flex items-center gap-1 text-[10px] text-slate-500">

            <Wind size={14}/>

            سرعة الرياح

          </div>

          <div className="font-extrabold mt-1">

            {weather.windSpeedKmh ?? '—'}
            {' '}
            <span className="text-xs font-normal">
              كم/س
            </span>

          </div>

          <div className="text-[9px] text-slate-500">

            {weather.windDirection || '—'}

            {weather.windDirectionDeg != null
              ? ` • ${weather.windDirectionDeg}°`
              : ''}

          </div>

        </div>


        <div className="p-3 border-l">

          <div className="flex items-center gap-1 text-[10px] text-slate-500">

            <Wind size={14}/>

            هبات الرياح

          </div>

          <div className="font-extrabold mt-1">

            {weather.windGustKmh ?? '—'}
            {' '}
            <span className="text-xs font-normal">
              كم/س
            </span>

          </div>

        </div>


        <div className="p-3">

          <div className="flex items-center gap-1 text-[10px] text-slate-500">

            <Droplets size={14}/>

            الرطوبة

          </div>

          <div className="font-extrabold mt-1">

            {weather.humidityPercent ?? '—'}%

          </div>

        </div>

      </div>

    </Card>
  );
}


function GuidanceCard({
  icon: Icon,
  title,
  value,
  tone = 'blue'
}) {

  const text =
    readable(value);

  if (!text) {
    return null;
  }


  const classes = {
    red:
      'border-red-200 bg-red-50',

    amber:
      'border-amber-200 bg-amber-50',

    blue:
      'border-blue-200 bg-blue-50',

    green:
      'border-emerald-200 bg-emerald-50',
  };


  return (
    <div
      className={`rounded-xl border p-3 ${classes[tone] || classes.blue}`}
    >

      <div className="flex items-center gap-2 font-extrabold text-[#1a4d80]">

        <Icon size={17}/>

        {title}

      </div>


      <div className="text-sm leading-7 text-slate-800 mt-2 whitespace-pre-line">

        {text}

      </div>

    </div>
  );
}



function NavigationMapFit({
  userPosition,
  factoryPosition,
  routePositions
}) {

  const map = useMap();

  useEffect(() => {

    const points = [];

    if (userPosition) {
      points.push(
        L.latLng(
          userPosition[0],
          userPosition[1]
        )
      );
    }

    if (factoryPosition) {
      points.push(
        L.latLng(
          factoryPosition[0],
          factoryPosition[1]
        )
      );
    }

    if (
      Array.isArray(routePositions) &&
      routePositions.length > 0
    ) {

      const step =
        Math.max(
          1,
          Math.floor(
            routePositions.length / 40
          )
        );

      for (
        let i = 0;
        i < routePositions.length;
        i += step
      ) {

        points.push(
          L.latLng(
            routePositions[i][0],
            routePositions[i][1]
          )
        );

      }

    }

    if (points.length >= 2) {

      map.fitBounds(
        L.latLngBounds(points),
        {
          padding: [35, 35],
          maxZoom: 16
        }
      );

    } else if (points.length === 1) {

      map.setView(
        points[0],
        16
      );

    }

  }, [
    map,
    userPosition,
    factoryPosition,
    routePositions
  ]);

  return null;
}


function FireNavigationMap({
  factory,
  stationName,
  userPosition,
  route,
  weather,
  loading,
  error,
  locationError,
  onBack,
  onRetryGps
}) {

  const [mapType, setMapType] =
    useState('street');

  const factoryPosition = [
    Number(factory.lat),
    Number(factory.lng)
  ];


  const distanceKm =
    route?.distanceM != null
      ? route.distanceM / 1000
      : null;


  const etaMinutes =
    route?.durationS != null
      ? Math.max(
          1,
          Math.round(
            route.durationS / 60
          )
        )
      : null;


  const windFromDeg =
    weather?.windDirectionDeg != null
      ? Number(
          weather.windDirectionDeg
        )
      : null;


  // Meteorological wind direction is where
  // the wind COMES FROM.
  // Arrow here shows where the wind MOVES TO.
  const windToDeg =
    Number.isFinite(windFromDeg)
      ? (
          windFromDeg + 180
        ) % 360
      : null;


  const windIcon =
    windToDeg != null
      ? L.divIcon({
          className: '',
          iconSize: [70, 70],
          iconAnchor: [35, 35],
          html: `
            <div style="
              width:70px;
              height:70px;
              display:flex;
              align-items:center;
              justify-content:center;
              pointer-events:none;
            ">
              <div style="
                width:50px;
                height:50px;
                border-radius:50%;
                background:rgba(255,255,255,.92);
                border:2px solid #0284c7;
                box-shadow:0 3px 12px rgba(15,23,42,.25);
                display:flex;
                align-items:center;
                justify-content:center;
              ">
                <div style="
                  font-size:30px;
                  line-height:1;
                  color:#0284c7;
                  font-weight:900;
                  transform:rotate(${windToDeg - 90}deg);
                ">➤</div>
              </div>
            </div>
          `
        })
      : null;


  return (
    <div
      dir="rtl"
      className="h-[100dvh] bg-slate-100 flex flex-col overflow-hidden"
    >

      {/* HEADER */}
      <div className="shrink-0 bg-[#1a4d80] text-white shadow-lg z-[1000]">

        <div className="px-3 py-2.5">

          <div className="flex items-center gap-2">

            <button
              type="button"
              onClick={onBack}
              className="w-10 h-10 shrink-0 rounded-xl bg-white/15 hover:bg-white/25 border border-white/20 flex items-center justify-center text-2xl font-bold"
              aria-label="رجوع"
            >
              ←
            </button>


            <div className="min-w-0 flex-1">

              <div className="text-[10px] text-blue-100">
                التوجه إلى موقع المصنع
              </div>

              <div className="font-black text-sm truncate">
                {factory.factoryName}
              </div>

              {stationName && (
                <div className="text-[9px] text-blue-100 truncate">
                  {stationName}
                </div>
              )}

            </div>


            <div className="bg-white/10 rounded-xl px-2.5 py-1.5 text-center">

              <div className="text-[9px] text-blue-100">
                الوصول
              </div>

              <div className="font-black text-sm whitespace-nowrap">
                {etaMinutes != null
                  ? `${etaMinutes} د`
                  : '—'}
              </div>

            </div>

          </div>

        </div>


        {/* ETA + DISTANCE */}
        <div className="grid grid-cols-2 border-t border-white/15">

          <div className="py-2 px-3 border-l border-white/15 text-center">

            <div className="text-[9px] text-blue-100">
              الوقت المتوقع للوصول
            </div>

            <div className="font-black text-base mt-0.5">

              {loading && etaMinutes == null
                ? 'جارٍ الحساب...'
                : etaMinutes != null
                  ? `${etaMinutes} دقيقة`
                  : '—'}

            </div>

          </div>


          <div className="py-2 px-3 text-center">

            <div className="text-[9px] text-blue-100">
              مسافة الطريق
            </div>

            <div className="font-black text-base mt-0.5">

              {distanceKm != null
                ? distanceKm < 10
                  ? `${distanceKm.toFixed(1)} كم`
                  : `${Math.round(distanceKm)} كم`
                : '—'}

            </div>

          </div>

        </div>

      </div>


      {/* MAP */}
      <div className="relative flex-1 min-h-0">

        <MapContainer
          center={factoryPosition}
          zoom={15}
          style={{
            height: '100%',
            width: '100%'
          }}
          zoomControl={true}
        >

          {mapType === 'street' ? (

            <TileLayer
              key="street"
              attribution="© OpenStreetMap"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

          ) : (

            <>

              <TileLayer
                key="satellite"
                attribution="Tiles © Esri"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />

              <TileLayer
                key="satellite-labels"
                attribution="Labels © Esri"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
              />

            </>

          )}


          {/* ACTUAL ROAD ROUTE */}
          {route?.positions?.length > 1 && (

            <Polyline
              positions={route.positions}
              pathOptions={{
                color: '#1a4d80',
                weight: 7,
                opacity: 0.88
              }}
            />

          )}


          {/* USER / FIRE TEAM */}
          {userPosition && (

            <CircleMarker
              center={userPosition}
              radius={9}
              pathOptions={{
                color: '#ffffff',
                weight: 4,
                fillColor: '#2563eb',
                fillOpacity: 1
              }}
            >

              <Tooltip
                permanent
                direction="top"
                offset={[0, -10]}
              >
                🚒 موقع فريق الإطفاء
              </Tooltip>

              <Popup>
                موقعك الحالي
              </Popup>

            </CircleMarker>

          )}


          {/* FACTORY */}
          <CircleMarker
            center={factoryPosition}
            radius={10}
            pathOptions={{
              color: '#ffffff',
              weight: 4,
              fillColor: '#dc2626',
              fillOpacity: 1
            }}
          >

            <Tooltip
              permanent
              direction="bottom"
              offset={[0, 10]}
            >
              🏭 {factory.factoryName}
            </Tooltip>

            <Popup>
              <div
                dir="rtl"
                style={{
                  minWidth: '160px'
                }}
              >
                <strong>
                  {factory.factoryName}
                </strong>

                {factory.activity && (
                  <div>
                    {factory.activity}
                  </div>
                )}
              </div>
            </Popup>

          </CircleMarker>


          {/* WIND DIRECTION AT FACTORY */}
          {windIcon && (

            <Marker
              position={factoryPosition}
              icon={windIcon}
              zIndexOffset={900}
            >

              <Popup>

                <div dir="rtl">

                  <div style={{
                    fontWeight: 800
                  }}>
                    اتجاه الرياح
                  </div>

                  <div>
                    {weather?.windSpeedKmh ?? '—'}
                    {' '}
                    كم/س
                  </div>

                  <div>
                    قادمة من:
                    {' '}
                    {weather?.windDirection || '—'}
                    {' '}
                    {windFromDeg != null
                      ? `(${windFromDeg}°)`
                      : ''}
                  </div>

                  <div>
                    اتجاه الحركة:
                    {' '}
                    {windToDeg != null
                      ? `${windToDeg}°`
                      : '—'}
                  </div>

                </div>

              </Popup>

            </Marker>

          )}


          <NavigationMapFit
            userPosition={userPosition}
            factoryPosition={factoryPosition}
            routePositions={
              route?.positions || []
            }
          />

        </MapContainer>


        {/* MAP TYPE SWITCHER */}
        <div className="absolute top-[92px] right-3 z-[850]">

          <div className="flex items-center rounded-lg border border-slate-200 bg-white/95 backdrop-blur shadow-md p-0.5">

            <button
              type="button"
              onClick={() =>
                setMapType('street')
              }
              className={`h-8 px-2 rounded-md text-[9px] font-black transition ${
                mapType === 'street'
                  ? 'bg-[#1a4d80] text-white'
                  : 'text-slate-600'
              }`}
            >
              🗺️ خريطة
            </button>

            <button
              type="button"
              onClick={() =>
                setMapType('satellite')
              }
              className={`h-8 px-2 rounded-md text-[9px] font-black transition ${
                mapType === 'satellite'
                  ? 'bg-[#1a4d80] text-white'
                  : 'text-slate-600'
              }`}
            >
              🛰️ قمر
            </button>

          </div>

        </div>


        {/* WIND STATUS */}
        {weather?.available && (

          <div className="absolute top-3 right-3 left-3 z-[800] pointer-events-none">

            <div className="max-w-sm mx-auto bg-white/95 backdrop-blur border border-slate-200 rounded-xl shadow-lg px-3 py-2">

              <div className="flex items-center justify-between gap-3">

                <div>

                  <div className="text-[9px] text-slate-500">
                    الرياح عند المصنع
                  </div>

                  <div className="font-black text-xs text-slate-800">

                    {weather.windSpeedKmh ?? '—'}
                    {' '}
                    كم/س

                    {' • '}

                    {weather.windDirection || '—'}

                    {weather.windDirectionDeg != null
                      ? ` ${weather.windDirectionDeg}°`
                      : ''}

                  </div>

                </div>


                {windToDeg != null && (

                  <div
                    className="w-10 h-10 rounded-full border-2 border-sky-500 bg-sky-50 flex items-center justify-center text-sky-600 text-2xl font-black"
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        transform:
                          `rotate(${windToDeg - 90}deg)`
                      }}
                    >
                      ➤
                    </span>
                  </div>

                )}

              </div>

              <div className="text-[8px] text-slate-400 mt-1">
                السهم يوضح اتجاه حركة الرياح
              </div>

            </div>

          </div>

        )}


        {/* ERRORS / STATUS */}
        {(error || locationError) && (

          <div className="absolute bottom-3 right-3 left-3 z-[900]">

            <div className="max-w-sm mx-auto rounded-xl border border-amber-200 bg-white/95 backdrop-blur shadow-lg p-2.5">

              <div className="flex items-center gap-2">

                <div className="w-8 h-8 shrink-0 rounded-lg bg-amber-100 flex items-center justify-center">
                  📍
                </div>

                <div className="min-w-0 flex-1">

                  <div className="text-[10px] font-black text-amber-900">
                    {locationError
                      ? 'GPS غير متاح'
                      : 'تعذر حساب المسار'}
                  </div>

                  <div className="text-[9px] leading-4 text-amber-700">
                    {locationError || error}
                  </div>

                </div>

              </div>


              {locationError && (

                <button
                  type="button"
                  onClick={onRetryGps}
                  className="mt-2 w-full h-9 rounded-lg bg-[#1a4d80] text-white text-[10px] font-black"
                >
                  📍 تفعيل GPS / إعادة المحاولة
                </button>

              )}

            </div>

          </div>

        )}

      </div>


      {/* BOTTOM INFO */}
      <div className="shrink-0 bg-white border-t border-slate-200 px-3 py-2">

        <div className="flex items-center justify-between gap-3">

          <div className="min-w-0">

            <div className="text-[9px] text-slate-500">
              حالة الملاحة
            </div>

            <div className="text-[11px] font-bold text-slate-700 truncate">

              {!userPosition
                ? 'جارٍ تحديد موقع فريق الإطفاء...'
                : loading
                  ? 'جارٍ تحديث أفضل مسار...'
                  : route
                    ? 'المسار محدّث حسب موقع الفريق'
                    : 'بانتظار حساب المسار'}

            </div>

          </div>


          <div className="text-left shrink-0">

            <div className="text-[9px] text-slate-400">
              GPS
            </div>

            <div className={`text-[10px] font-black ${
              userPosition
                ? 'text-emerald-600'
                : 'text-amber-600'
            }`}>
              {userPosition
                ? 'نشط'
                : 'انتظار'}
            </div>

          </div>

        </div>

      </div>

    </div>
  );
}


export default function FireResponsePortal() {

  const {
    token
  } = useParams();


  const [station, setStation] =
    useState(null);

  const [accessName, setAccessName] =
    useState('');

  const [factories, setFactories] =
    useState([]);

  const [query, setQuery] =
    useState('');

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [expanded, setExpanded] =
    useState(null);

  const [detail, setDetail] =
    useState({});

  const [darkMode, setDarkMode] =
    useState(() => {

      try {

        const saved =
          localStorage.getItem(
            'fire-portal-theme'
          );

        if (saved === 'dark') {
          return true;
        }

        if (saved === 'light') {
          return false;
        }

        return (
          window.matchMedia &&
          window.matchMedia(
            '(prefers-color-scheme: dark)'
          ).matches
        );

      } catch {
        return false;
      }

    });


  useEffect(() => {

    try {

      localStorage.setItem(
        'fire-portal-theme',
        darkMode
          ? 'dark'
          : 'light'
      );

    } catch {
      // Storage may be unavailable.
    }


    document.documentElement.style.colorScheme =
      darkMode
        ? 'dark'
        : 'light';


    let meta =
      document.querySelector(
        'meta[name="theme-color"]'
      );

    if (!meta) {

      meta =
        document.createElement(
          'meta'
        );

      meta.setAttribute(
        'name',
        'theme-color'
      );

      document.head.appendChild(
        meta
      );

    }

    meta.setAttribute(
      'content',
      darkMode
        ? '#071827'
        : '#1a4d80'
    );

  }, [
    darkMode
  ]);


  const [navTarget, setNavTarget] =
    useState(null);

  const [navPosition, setNavPosition] =
    useState(null);

  const [navRoute, setNavRoute] =
    useState(null);

  const [navWeather, setNavWeather] =
    useState(null);

  const [navLoading, setNavLoading] =
    useState(false);

  const [navError, setNavError] =
    useState('');

  const [navLocationError, setNavLocationError] =
    useState('');

  const [navGpsRetry, setNavGpsRetry] =
    useState(0);

  const lastRouteAt =
    useRef(0);



  useEffect(() => {

    const loadStation = async () => {

      try {

        const r =
          await axios.get(
            `/api/fire-portal/${token}/station`
          );

        setStation(
          r.data.station
        );

        setAccessName(
          r.data.access?.name ||
          ''
        );

      } catch (e) {

        setError(
          e?.response?.data?.detail ||
          'تعذر فتح بوابة فريق الإطفاء'
        );
      }
    };


    loadStation();

  }, [token]);


  useEffect(() => {

    const timer =
      setTimeout(
        async () => {

          try {

            setLoading(true);

            const params = {};

            if (
              query.trim()
            ) {
              params.q =
                query.trim();
            }


            const r =
              await axios.get(
                `/api/fire-portal/${token}/factories`,
                {
                  params
                }
              );


            setFactories(
              Array.isArray(
                r.data
              )
                ? r.data
                : []
            );

            setError('');

          } catch (e) {

            setError(
              e?.response?.data?.detail ||
              'تعذر تحميل المصانع'
            );

          } finally {

            setLoading(false);
          }

        },
        300
      );


    return () =>
      clearTimeout(
        timer
      );

  }, [
    token,
    query
  ]);


  const openDetail =
    async (factory) => {

      if (
        expanded ===
        factory.id
      ) {

        setExpanded(null);

        return;
      }


      setExpanded(
        factory.id
      );


      if (
        detail[
          factory.id
        ]
      ) {
        return;
      }


      try {

        const r =
          await axios.get(
            `/api/fire-portal/${token}/factories/${factory.id}`
          );


        setDetail(
          prev => ({
            ...prev,

            [factory.id]:
              r.data
          })
        );

      } catch (e) {

        setError(
          e?.response?.data?.detail ||
          'تعذر تحميل بيانات المصنع'
        );
      }
    };



  // ========================================================
  // INTERNAL NAVIGATION:
  // GPS + factory-specific weather
  // ========================================================

  useEffect(() => {

    if (!navTarget) {
      return undefined;
    }

    let active = true;
    let watchId = null;


    setNavRoute(null);
    setNavError('');
    setNavLocationError('');
    lastRouteAt.current = 0;


    const cached =
      detail?.[navTarget.id];

    if (cached?.weather) {

      setNavWeather(
        cached.weather
      );

    } else {

      axios.get(
        `/api/fire-portal/${token}/factories/${navTarget.id}`
      )
        .then(response => {

          if (!active) {
            return;
          }

          setNavWeather(
            response?.data?.weather ||
            null
          );

        })
        .catch(() => {

          if (active) {
            setNavWeather(null);
          }

        });

    }


    if (!navigator.geolocation) {

      setNavLocationError(
        'هذا الجهاز لا يدعم تحديد الموقع GPS.'
      );

      return () => {
        active = false;
      };
    }


    watchId =
      navigator.geolocation.watchPosition(

        position => {

          if (!active) {
            return;
          }

          setNavLocationError('');

          setNavPosition([
            position.coords.latitude,
            position.coords.longitude
          ]);

        },

        () => {

          if (!active) {
            return;
          }

          setNavLocationError(
            'تعذر تحديد موقع فريق الإطفاء. فعّل إذن الموقع GPS للمتصفح.'
          );

        },

        {
          enableHighAccuracy: true,
          maximumAge: 3000,
          timeout: 15000
        }

      );


    return () => {

      active = false;

      if (watchId != null) {
        navigator.geolocation.clearWatch(
          watchId
        );
      }

    };

  }, [
    navTarget,
    token,
    navGpsRetry
  ]);


  // ========================================================
  // ROAD ROUTE + ETA
  // Uses actual road network.
  // Updates as the fire team moves.
  // ========================================================

  useEffect(() => {

    if (
      !navTarget ||
      !navPosition
    ) {
      return undefined;
    }


    const targetLat =
      Number(navTarget.lat);

    const targetLng =
      Number(navTarget.lng);


    if (
      !Number.isFinite(targetLat) ||
      !Number.isFinite(targetLng)
    ) {

      setNavError(
        'إحداثيات المصنع غير صحيحة.'
      );

      return undefined;
    }


    const now = Date.now();

    // Avoid excessive routing requests while GPS updates.
    if (
      lastRouteAt.current &&
      now - lastRouteAt.current < 8000
    ) {
      return undefined;
    }

    lastRouteAt.current = now;


    const controller =
      new AbortController();


    const loadRoute = async () => {

      setNavLoading(true);
      setNavError('');


      try {

        const userLat =
          navPosition[0];

        const userLng =
          navPosition[1];


        const url =
          `https://router.project-osrm.org/route/v1/driving/` +
          `${userLng},${userLat};${targetLng},${targetLat}` +
          `?overview=full&geometries=geojson&steps=false&alternatives=false`;


        const response =
          await fetch(
            url,
            {
              signal:
                controller.signal
            }
          );


        if (!response.ok) {
          throw new Error(
            'route service error'
          );
        }


        const data =
          await response.json();


        const best =
          data?.routes?.[0];


        if (
          !best ||
          !best.geometry?.coordinates
        ) {
          throw new Error(
            'route not found'
          );
        }


        const positions =
          best.geometry.coordinates.map(
            coordinate => [
              coordinate[1],
              coordinate[0]
            ]
          );


        setNavRoute({
          positions,
          distanceM:
            Number(best.distance || 0),
          durationS:
            Number(best.duration || 0)
        });


      } catch (e) {

        if (
          e?.name !== 'AbortError'
        ) {

          setNavError(
            'تعذر حساب مسار الطريق حاليًا. موقع المصنع ما زال ظاهرًا على الخريطة.'
          );

        }

      } finally {

        setNavLoading(false);

      }

    };


    loadRoute();


    return () => {
      controller.abort();
    };

  }, [
    navTarget,
    navPosition
  ]);


  const navigateTo =
    factory => {

      if (
        factory.lat == null ||
        factory.lng == null
      ) {
        return;
      }

      const destination =
        `${factory.lat},${factory.lng}`;

      const url =
        `https://www.google.com/maps/dir/?api=1` +
        `&destination=${encodeURIComponent(destination)}` +
        `&travelmode=driving` +
        `&dir_action=navigate`;

      window.open(
        url,
        '_blank',
        'noopener,noreferrer'
      );
    };


  if (navTarget) {

    return (
      <FireNavigationMap
        factory={navTarget}
        stationName={
          station?.name || ''
        }
        userPosition={navPosition}
        route={navRoute}
        weather={navWeather}
        loading={navLoading}
        error={navError}
        locationError={
          navLocationError
        }
        onRetryGps={() => {

          setNavLocationError('');
          setNavError('');
          setNavPosition(null);
          setNavRoute(null);

          setNavGpsRetry(
            value => value + 1
          );

        }}

        onBack={() => {

          setNavTarget(null);
          setNavPosition(null);
          setNavRoute(null);
          setNavWeather(null);
          setNavError('');
          setNavLocationError('');

        }}
      />
    );

  }


  return (
    <div
      dir="rtl"
      className={`fire-portal-shell ${
        darkMode
          ? 'fire-portal-dark'
          : 'fire-portal-light'
      }`}
    >

      <header className="fire-portal-header relative text-white sticky top-0 z-20 shadow-lg">
        <button
          type="button"
          onClick={() =>
            setDarkMode(
              value => !value
            )
          }
          className="fire-theme-toggle"
          title={
            darkMode
              ? 'الوضع الفاتح'
              : 'الوضع الداكن'
          }
          aria-label={
            darkMode
              ? 'تفعيل الوضع الفاتح'
              : 'تفعيل الوضع الداكن'
          }
        >
          <span className="text-base leading-none">
            {darkMode
              ? '☀️'
              : '🌙'}
          </span>

          <span>
            {darkMode
              ? 'فاتح'
              : 'داكن'}
          </span>
        </button>



        <div className="max-w-3xl mx-auto px-4 py-3">

          <div className="flex items-center gap-3">

            <div className="bg-white rounded-lg p-1">

              <ModonLogo
                size={40}
              />

            </div>


            <div className="min-w-0">

              <div className="font-extrabold truncate">

                {station?.name ||
                  'بوابة فريق الإطفاء'}

              </div>

              <div className="text-[11px] text-blue-100 truncate">

                {accessName ||
                  'الوصول الميداني للطوارئ'}

              </div>

            </div>

          </div>

        </div>

      </header>


      <main className="max-w-3xl mx-auto p-3 space-y-3">

        {error && (

          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm font-bold">

            {error}

          </div>

        )}


        {/* SEARCH */}
        <Card className="p-3 sticky top-[68px] z-10 shadow-md">

          <div className="relative">

            <Search
              size={18}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
            />


            <Input
              value={query}
              onChange={
                e =>
                  setQuery(
                    e.target.value
                  )
              }
              className="pr-10 h-11"
              placeholder="ابحث باسم المصنع أو المادة أو رقم UN..."
              autoComplete="off"
            />

          </div>

        </Card>


        <div className="grid grid-cols-2 gap-2">

          <Card className="p-3">

            <div className="text-[11px] text-slate-500">
              المصانع
            </div>

            <div className="font-extrabold text-xl text-[#1a4d80]">
              {factories.length}
            </div>

          </Card>


          <Card className="p-3">

            <div className="text-[11px] text-slate-500">
              المحطة
            </div>

            <div className="font-bold text-sm truncate mt-1">

              {station?.code ||
                station?.name ||
                '—'}

            </div>

          </Card>

        </div>


        {loading ? (

          <Card className="p-8 text-center text-slate-500">

            جارٍ تحميل بيانات المصانع...

          </Card>

        ) : factories.length === 0 ? (

          <Card className="p-8 text-center">

            <Building2
              size={38}
              className="mx-auto text-slate-300"
            />

            <div className="font-bold mt-2">
              لا توجد نتائج
            </div>

          </Card>

        ) : (

          factories.map(
            factory => {

              const full =
                detail[
                  factory.id
                ];


              const isOpen =
                expanded ===
                factory.id;


              return (
                <Card
                  key={
                    factory.id
                  }
                  className="overflow-hidden"
                >

                  <div className="p-4">

                    <div className="flex items-start justify-between gap-2">

                      <div>

                        <h2 className="font-extrabold text-lg text-slate-900">

                          {factory.factoryName}

                        </h2>

                        <div className="text-xs text-slate-500 mt-1">

                          {factory.activity || '—'}

                        </div>

                      </div>


                      <Badge
                        className={
                          riskClass(
                            factory.riskLevel
                          )
                        }
                      >

                        {factory.riskLabel}

                        {' '}

                        {factory.riskScore}

                      </Badge>

                    </div>


                    <div className="grid grid-cols-2 gap-2 mt-3">

                      <div className="rounded-lg bg-slate-50 p-2 text-center">

                        <FlaskConical
                          size={15}
                          className="mx-auto text-violet-600"
                        />

                        <div className="text-[10px] text-slate-500 mt-1">
                          المواد
                        </div>

                        <div className="font-bold">
                          {factory.chemicalCount}
                        </div>

                      </div>


                      <div className="rounded-lg bg-slate-50 p-2 text-center">

                        <ShieldAlert
                          size={15}
                          className="mx-auto text-[#1a4d80]"
                        />

                        <div className="text-[10px] text-slate-500 mt-1">
                          تقارير SDS
                        </div>

                        <div className="font-bold">
                          {factory.sdsCount}
                        </div>

                      </div>

                    </div>


                    {/* FAST QUANTITY PREVIEW */}
                    {factory.chemicalPreview?.length > 0 && (

                      <div className="mt-3">

                        <div className="text-xs font-bold text-slate-600 mb-2">
                          المواد والكميات
                        </div>


                        <div className="space-y-1">

                          {factory.chemicalPreview.map(
                            (
                              chem,
                              i
                            ) => (

                              <div
                                key={i}
                                className="flex justify-between items-center gap-2 text-xs bg-slate-50 rounded-lg p-2"
                              >

                                <div className="min-w-0 flex-1">

                                  <div className="flex items-center gap-2 flex-wrap">

                                    <div className="font-bold">
                                      {chem.name}
                                    </div>

                                    <ChemicalHazardMarks
                                      chem={chem}
                                      compact
                                    />

                                  </div>

                                  {chem.unNumber && (
                                    <div className="text-[10px] text-slate-500 mt-1">
                                      UN {chem.unNumber}
                                    </div>
                                  )}

                                </div>


                                <ChemicalQuantity
                                  chem={chem}
                                  compact
                                />

                              </div>

                            )
                          )}

                        </div>

                      </div>

                    )}


                    <div className="grid grid-cols-2 gap-2 mt-4">

                      <Button
                        variant="outline"
                        onClick={() =>
                          navigateTo(
                            factory
                          )
                        }
                        disabled={
                          factory.lat == null ||
                          factory.lng == null
                        }
                      >

                        <Navigation size={14}/>

                        الانتقال للموقع

                      </Button>


                      <Button
                        className="bg-[#1a4d80]"
                        onClick={() =>
                          openDetail(
                            factory
                          )
                        }
                      >

                        {isOpen
                          ? <ChevronUp size={14}/>
                          : <ChevronDown size={14}/>}

                        {isOpen
                          ? 'إغلاق التفاصيل'
                          : 'تفاصيل الطوارئ'}

                      </Button>

                    </div>

                  </div>


                  {isOpen && (

                    <div className="border-t bg-slate-50 p-3">

                      {!full ? (

                        <div className="text-sm text-slate-500 text-center py-6">

                          جارٍ تحميل تفاصيل الطوارئ...

                        </div>

                      ) : (

                        <div className="space-y-2">


                          {/* EXACT FACTORY WEATHER */}
                          <WeatherBar
                            weather={
                              full.weather
                            }
                            title="الطقس عند موقع المصنع"
                          />


                          <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-[11px] text-blue-800">

                            المعلومات التشغيلية أدناه مأخوذة من بيانات السلامة المسجلة للمصنع. لا يقوم النظام بإنشاء تعليمات كيميائية غير موجودة في بيانات المصنع.

                          </div>


                          {/* LOCATION */}
                          <div className="bg-white border rounded-xl p-3">

                            <div className="flex items-center gap-2 font-extrabold text-[#1a4d80]">

                              <MapPin size={16}/>

                              موقع المصنع

                            </div>


                            <div className="text-sm mt-2">

                              {full.address ||
                                'العنوان غير مسجل'}

                            </div>


                            {full.phone && (

                              <div className="text-xs text-slate-500 mt-2">

                                هاتف المصنع:
                                {' '}

                                <a
                                  className="text-[#1a4d80] underline font-bold"
                                  href={`tel:${full.phone}`}
                                >

                                  {full.phone}

                                </a>

                              </div>

                            )}

                          </div>


                          {/* ALL CHEMICALS + QUANTITIES */}
                          <div>

                            <div className="flex items-center justify-between gap-2 mb-2">

                              <div className="flex items-center gap-2">

                                <div className="w-8 h-8 rounded-lg bg-[#1a4d80] text-white flex items-center justify-center">

                                  <FlaskConical size={16}/>

                                </div>

                                <div>

                                  <div className="font-black text-sm text-[#1a4d80]">
                                    المواد الكيميائية
                                  </div>

                                  <div className="text-[9px] text-slate-500">
                                    الكميات الفعلية ورموز الخطورة
                                  </div>

                                </div>

                              </div>


                              <Badge
                                variant="secondary"
                                className="font-black"
                              >
                                {full.chemicals?.length || 0} مادة
                              </Badge>

                            </div>


                            {full.chemicals?.length > 0 ? (

                              <div className="space-y-2">

                                {full.chemicals.map(
                                  (
                                    chem,
                                    index
                                  ) => (

                                    <div
                                      key={index}
                                      className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                                    >

                                      {/* MATERIAL HEADER */}
                                      <div className="px-2.5 pt-2.5 pb-1.5">

                                        <div className="flex items-start justify-between gap-2">

                                          <div className="min-w-0 flex-1">

                                            <div className="flex items-center gap-2 flex-wrap">

                                              <div className="font-black text-sm text-slate-900 leading-tight">

                                                {chem.name}

                                              </div>

                                              <ChemicalHazardMarks
                                                chem={chem}
                                                compact
                                              />

                                            </div>


                                            {chem.chemicalName &&
                                             chem.chemicalName !== chem.name && (

                                              <div className="text-[11px] text-slate-500 mt-1">

                                                {chem.chemicalName}

                                              </div>

                                            )}

                                          </div>

                                        </div>


                                        {/* UN + CAS */}
                                        {(chem.unNumber ||
                                          chem.casNumber) && (

                                          <div className="flex flex-wrap gap-1 mt-1.5">

                                            {chem.unNumber && (

                                              <span
                                                dir="ltr"
                                                className="rounded bg-orange-50 border border-orange-200 px-1.5 py-0.5 text-[9px] font-black text-orange-800"
                                              >
                                                UN {chem.unNumber}
                                              </span>

                                            )}


                                            {chem.casNumber && (

                                              <span
                                                dir="ltr"
                                                className="rounded bg-slate-50 border border-slate-200 px-1.5 py-0.5 text-[9px] font-bold text-slate-600"
                                              >
                                                CAS {chem.casNumber}
                                              </span>

                                            )}

                                          </div>

                                        )}

                                      </div>


                                      {/* ACTUAL QUANTITY */}
                                      <div className="px-2.5 pb-2.5">

                                        <ChemicalQuantity
                                          chem={chem}
                                          detail
                                        />

                                      </div>

                                    </div>

                                  )
                                )}

                              </div>

                            ) : (

                              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-center text-xs text-slate-500">

                                لا توجد مواد كيميائية مسجلة.

                              </div>

                            )}

                          </div>


                          {/* EMERGENCY PLAN */}
                          <GuidanceCard
                            icon={AlertTriangle}
                            title="خطة الطوارئ"
                            value={
                              full.emergency?.emergencyPlan
                            }
                            tone="red"
                          />

                          {/* OPERATIONAL SCENARIOS */}
                          <div>

                            <div className="flex items-center gap-1.5 text-sm font-extrabold text-[#1a4d80] mb-2">

                              <AlertTriangle size={16}/>

                              سيناريوهات التعامل

                            </div>


                            {full.operationalScenarios?.length > 0 ? (

                              <div className="space-y-2">

                                {full.operationalScenarios.map(
                                  scenario => (

                                    <div
                                      key={
                                        scenario.key
                                      }
                                      className="bg-white border border-amber-200 rounded-xl p-3"
                                    >

                                      <div className="font-extrabold text-amber-800">

                                        {scenario.title}

                                      </div>


                                      <div className="text-sm leading-7 mt-2 text-slate-800">

                                        {scenario.instructions}

                                      </div>


                                      {scenario.ppe && (

                                        <div className="mt-3 bg-slate-50 rounded-lg p-2 text-xs">

                                          <b>
                                            معدات الوقاية:
                                          </b>

                                          {' '}

                                          {scenario.ppe}

                                        </div>

                                      )}

                                    </div>

                                  )
                                )}

                              </div>

                            ) : (

                              <div className="bg-white border rounded-xl p-4 text-sm text-slate-500">

                                لا توجد سيناريوهات تشغيلية مسجلة في بيانات المصنع.

                              </div>

                            )}

                          </div>






                        </div>

                      )}

                    </div>

                  )}

                </Card>
              );
            }
          )

        )}

      </main>

    </div>
  );
}
